import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useSearch, useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { MessageSquare, Plus, Eye, Send, Lock } from "lucide-react";
import type { SupportMessage } from "@shared/schema";

interface SupportReply {
  id: string;
  supportMessageId: string;
  sender: string;
  message: string;
  createdAt: string;
}

export default function ClientSupport() {
  const { toast } = useToast();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<SupportMessage | null>(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [replyText, setReplyText] = useState("");
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const autoOpenedRef = useRef(false);

  const { data: messages, isLoading } = useQuery<SupportMessage[]>({
    queryKey: ["/api/client/support"],
  });

  useEffect(() => {
    if (!messages || autoOpenedRef.current) return;
    const params = new URLSearchParams(searchString);
    const ticketId = params.get("ticket");
    if (ticketId) {
      const ticket = messages.find(m => m.id === ticketId);
      if (ticket) {
        setSelectedMessage(ticket);
        autoOpenedRef.current = true;
        setLocation("/support", { replace: true });
      }
    }
  }, [messages, searchString, setLocation]);

  const { data: replies } = useQuery<SupportReply[]>({
    queryKey: ["/api/client/support", selectedMessage?.id, "replies"],
    queryFn: () => fetch(`/api/client/support/${selectedMessage!.id}/replies`, { credentials: "include" }).then(r => r.json()),
    enabled: !!selectedMessage,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/client/support", { subject, message });
    },
    onSuccess: () => {
      toast({ title: "Meddelande skickat!", description: "Vi återkommer så snart som möjligt." });
      queryClient.invalidateQueries({ queryKey: ["/api/client/support"] });
      setCreateDialogOpen(false);
      setSubject("");
      setMessage("");
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte skicka meddelandet", variant: "destructive" });
    },
  });

  const replyMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", `/api/client/support/${selectedMessage!.id}/reply`, { message: replyText });
    },
    onSuccess: () => {
      toast({ title: "Svar skickat!" });
      queryClient.invalidateQueries({ queryKey: ["/api/client/support", selectedMessage?.id, "replies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/client/support"] });
      setReplyText("");
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte skicka svaret", variant: "destructive" });
    },
  });

  const getStatusBadge = (status: string) => {
    if (status === "open") return <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">Skickat</Badge>;
    if (status === "answered") return <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20">Besvarat</Badge>;
    return <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">Stängt</Badge>;
  };

  const openTicket = (msg: SupportMessage) => {
    setSelectedMessage(msg);
    setReplyText("");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Support</h1>
          <p className="text-muted-foreground">Skicka meddelanden till oss</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} data-testid="button-new-support">
          <Plus className="h-4 w-4 mr-2" />
          Nytt meddelande
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="pt-4">
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : messages && messages.length > 0 ? (
        <div className="space-y-3">
          {messages.map((msg) => (
            <Card key={msg.id} className="hover-elevate cursor-pointer" onClick={() => openTicket(msg)}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <MessageSquare className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{msg.subject}</p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(msg.createdAt!), "d MMMM yyyy, HH:mm", { locale: sv })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(msg.status)}
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); openTicket(msg); }}
                      data-testid={`button-view-support-${msg.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Du har inga supportärenden</p>
            <p className="text-sm text-muted-foreground mt-2">Klicka "Nytt meddelande" för att kontakta oss</p>
          </CardContent>
        </Card>
      )}

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nytt meddelande</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Ämne</label>
              <Input
                placeholder="Vad gäller ditt ärende?"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="input-support-subject"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Meddelande</label>
              <Textarea
                placeholder="Beskriv ditt ärende..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                data-testid="input-support-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Avbryt</Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending || !subject.trim() || !message.trim()}
              data-testid="button-send-support"
            >
              {createMutation.isPending ? "Skickar..." : "Skicka"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <DialogTitle>{selectedMessage?.subject}</DialogTitle>
              {selectedMessage && getStatusBadge(selectedMessage.status)}
            </div>
          </DialogHeader>
          {selectedMessage && (
            <div className="flex flex-col flex-1 min-h-0">
              <div className="flex-1 overflow-y-auto space-y-3 pr-1">
                <div className="flex justify-end">
                  <div className="max-w-[85%]">
                    <div className="bg-primary text-primary-foreground rounded-lg rounded-tr-sm p-3">
                      <p className="text-sm whitespace-pre-wrap">{selectedMessage.message}</p>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1 text-right">
                      Du - {format(new Date(selectedMessage.createdAt!), "d MMM HH:mm", { locale: sv })}
                    </p>
                  </div>
                </div>

                {selectedMessage.adminResponse && (!replies || replies.length === 0) && (
                  <div className="flex justify-start">
                    <div className="max-w-[85%]">
                      <div className="bg-muted rounded-lg rounded-tl-sm p-3">
                        <p className="text-sm whitespace-pre-wrap">{selectedMessage.adminResponse}</p>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Admin - {selectedMessage.respondedAt && format(new Date(selectedMessage.respondedAt), "d MMM HH:mm", { locale: sv })}
                      </p>
                    </div>
                  </div>
                )}

                {replies && replies.map((reply) => (
                  <div key={reply.id} className={`flex ${reply.sender === "client" ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[85%]">
                      <div className={`rounded-lg p-3 ${
                        reply.sender === "client"
                          ? "bg-primary text-primary-foreground rounded-tr-sm"
                          : "bg-muted rounded-tl-sm"
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{reply.message}</p>
                      </div>
                      <p className={`text-[11px] text-muted-foreground mt-1 ${reply.sender === "client" ? "text-right" : ""}`}>
                        {reply.sender === "client" ? "Du" : "Admin"} - {format(new Date(reply.createdAt), "d MMM HH:mm", { locale: sv })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {selectedMessage.status !== "closed" ? (
                <div className="flex items-end gap-2 mt-3 pt-3 border-t">
                  <Textarea
                    placeholder="Skriv ditt svar..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={2}
                    className="flex-1"
                    data-testid="input-client-reply"
                  />
                  <Button
                    size="icon"
                    onClick={() => replyMutation.mutate()}
                    disabled={replyMutation.isPending || !replyText.trim()}
                    data-testid="button-send-reply"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t text-sm text-muted-foreground">
                  <Lock className="h-4 w-4" />
                  <span>Ärendet är stängt</span>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
